import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { UploadCloud, CheckCircle2, XCircle, FileVideo, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * BulkUploadDialog — upload multiple video files at once.
 * Each file becomes a culte draft with title = filename, date = today, preacher provided by user.
 */
export default function BulkUploadDialog({ open, onOpenChange, onDone }) {
  const [defaultPreacher, setDefaultPreacher] = useState("");
  const [defaultTheme, setDefaultTheme] = useState("");
  const [files, setFiles] = useState([]); // { id, file, name, progress, status, error, culteId }
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const addFiles = (list) => {
    const arr = Array.from(list || []).filter((f) => f.type.startsWith("video/"));
    const items = arr.map((f) => ({
      id: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`,
      file: f,
      name: f.name.replace(/\.[^.]+$/, ""),
      progress: 0,
      status: "pending",
      error: null,
    }));
    setFiles((prev) => [...prev, ...items]);
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const rename = (id, name) => setFiles((prev) => prev.map((f) => f.id === id ? { ...f, name } : f));

  const uploadAll = async () => {
    if (!defaultPreacher.trim()) {
      toast.error("Renseignez d'abord le prédicateur");
      return;
    }
    if (files.length === 0) return;
    setUploading(true);
    const today = new Date().toISOString().slice(0, 10);

    for (const item of files) {
      if (item.status === "done") continue;
      // Update to uploading
      setFiles((prev) => prev.map((f) => f.id === item.id ? { ...f, status: "uploading", progress: 0 } : f));
      try {
        const fd = new FormData();
        fd.append("file", item.file);
        const { data } = await api.post("/media/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) {
              const p = Math.round((e.loaded * 100) / e.total);
              setFiles((prev) => prev.map((f) => f.id === item.id ? { ...f, progress: p } : f));
            }
          },
        });
        // Create the culte
        const culteRes = await api.post("/cultes", {
          title: item.name || item.file.name,
          date: today,
          preacher: defaultPreacher,
          theme: defaultTheme || null,
          video_type: "upload",
          video_url: data.url,
        });
        setFiles((prev) => prev.map((f) => f.id === item.id ? { ...f, status: "done", progress: 100, culteId: culteRes.data.id } : f));
      } catch (e) {
        const err = formatApiErrorDetail(e.response?.data?.detail) || "Erreur";
        setFiles((prev) => prev.map((f) => f.id === item.id ? { ...f, status: "error", error: err } : f));
      }
    }
    setUploading(false);
    onDone?.();
    toast.success("Téléversement terminé");
  };

  const reset = () => {
    setFiles([]);
    setDefaultPreacher("");
    setDefaultTheme("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!uploading) { onOpenChange(o); if (!o) reset(); } }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Téléversement multiple de cultes</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Prédicateur (appliqué à tous) *</Label>
              <Input value={defaultPreacher} onChange={(e) => setDefaultPreacher(e.target.value)}
                placeholder="Pasteur Jean Bernard" className="mt-1.5" data-testid="bulk-preacher" />
            </div>
            <div>
              <Label>Thème (facultatif)</Label>
              <Input value={defaultTheme} onChange={(e) => setDefaultTheme(e.target.value)}
                placeholder="Foi, Grâce, Amour…" className="mt-1.5" data-testid="bulk-theme" />
            </div>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
            className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
              dragging ? "border-gold bg-gold/10" : "border-slate-300 bg-slate-50 hover:border-deepblue-600 hover:bg-blue-50"
            }`}
          >
            <UploadCloud className="w-10 h-10 mx-auto text-deepblue-600 mb-2" />
            <div className="font-semibold text-deepblue-900">Glissez plusieurs vidéos ou cliquez pour sélectionner</div>
            <div className="text-xs text-slate-500 mt-1">Chaque vidéo créera automatiquement un culte</div>
            <input
              type="file"
              accept="video/*"
              multiple
              hidden
              id="bulk-file-input"
              data-testid="bulk-file-input"
              onChange={(e) => addFiles(e.target.files)}
            />
            <Button asChild variant="outline" className="mt-3">
              <label htmlFor="bulk-file-input" className="cursor-pointer">Sélectionner des fichiers</label>
            </Button>
          </div>

          {files.length > 0 && (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
              {files.map((f) => (
                <div key={f.id} className="flex items-center gap-3 p-3 rounded-lg bg-white border border-slate-200" data-testid={`bulk-item-${f.id}`}>
                  <FileVideo className="w-5 h-5 text-deepblue-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <Input value={f.name} onChange={(e) => rename(f.id, e.target.value)} disabled={uploading}
                      className="h-8 text-sm mb-1" />
                    <div className="flex items-center gap-2">
                      {f.status === "uploading" && <Progress value={f.progress} className="h-1.5 flex-1" />}
                      {f.status === "pending" && <span className="text-xs text-slate-500">En attente</span>}
                      {f.status === "done" && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Culte créé</span>}
                      {f.status === "error" && <span className="text-xs text-red-600 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> {f.error}</span>}
                      {f.status === "uploading" && <span className="text-xs text-slate-500 w-10">{f.progress}%</span>}
                    </div>
                  </div>
                  {!uploading && (
                    <Button size="icon" variant="ghost" onClick={() => removeFile(f.id)}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { if (!uploading) { onOpenChange(false); reset(); } }} disabled={uploading}>
            Fermer
          </Button>
          <Button
            onClick={uploadAll}
            disabled={uploading || files.length === 0 || !defaultPreacher.trim()}
            className="bg-gold hover:bg-gold-hover text-white"
            data-testid="bulk-upload-btn"
          >
            <UploadCloud className="w-4 h-4 mr-2" />
            {uploading ? "Téléversement…" : `Téléverser ${files.filter((f) => f.status !== "done").length} vidéo(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
